import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import _orderBy from "lodash/orderBy";
import _get from "lodash/get";

import Tabs from "components/lib/Shared/Tabs";
import { calculateCumulativeOwnership, groupBeneficialOwners } from "utils";
import BeneficialOwnersTableReview from "./BeneficialOwnersTableReview";
import {
  CORPORATES,
  INDIVIDUALS,
} from "components/PLCTABS/SFLdata/config/constants";
import StepButtons from "components/lib/Global/StepButtons";

const tabs = [
  { id: "reportableBO", label: "Reportable BO" },
  { id: "nonReportableBO", label: "Non-Reportable BO" },
];

const BeneficialOwnership = (props) => {
  const {
    groupedBeneficialOwners,
    isInManualUpdate,
    transactions,
    beneficialOwners,
    goToNextParentTab,
    goToPrevParentTab,
  } = props;
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const isFirstStep = selectedTabIndex === 0;
  const isLastStep = selectedTabIndex === tabs.length - 1;
  const beneficialOwnersFromStore = useSelector(
    (state) => state.beneficialOwnersTableSlice.beneficialOwners
  );

  // const beneficialOwners = props.beneficialOwners ?? beneficialOwnersFromStore ?? [];

  const [reportableBO, setReportableBO] = useState([]);
  const [nonReportableBO, setNonReportableBO] = useState([]);

  // Helper function to get beneficial ownership data for manual update mode
  const getBeneficialOwnershipDataForManualUpdate = React.useCallback(
    (beneficialOwners, transactions) => {
      if (isInManualUpdate) {
      }

      if (!isInManualUpdate || !transactions?.length) {
        if (isInManualUpdate) {
        }
        return beneficialOwners; // Use existing logic
      }

      // Extract allocation data from transactions
      const transactionAllocations = new Map();
      transactions.forEach((transaction) => {
        if (
          transaction?.transactionType === "MANUAL" &&
          transaction?.shareCapitalChanges
        ) {
          transaction.shareCapitalChanges.forEach((change) => {
            if (change.allocations) {
              change.allocations.forEach((allocation) => {
                let officialId = allocation?.officialId;
                // Handle both string and ObjectId formats
                if (typeof officialId === "object" && officialId?.$oid) {
                  officialId = officialId.$oid;
                }

                if (officialId && allocation.beneficialOwnershipDetails) {
                  transactionAllocations.set(officialId, {
                    ...allocation.beneficialOwnershipDetails,
                    name: allocation.name,
                    allocation: allocation,
                  });
                }
              });
            }
          });
        }
      });

      // Merge transaction data with existing beneficial owners
      const result = beneficialOwners.map((bo) => {
        const transactionData = transactionAllocations.get(bo._id);
        if (transactionData) {
          if (isInManualUpdate) {
          }

          // Use updatedDetails as current data for display
          const updatedBeneficialOwnershipForm = {
            ...bo.beneficialOwnershipForm,
            ...transactionData.updatedDetails,
          };

          return {
            ...bo,
            // Preserve transaction name if available, otherwise use existing
            names: transactionData.name
              ? {
                  firstName: transactionData.name.split(" ")[0] || "",
                  lastName:
                    transactionData.name.split(" ").slice(1).join(" ") || "",
                }
              : bo.names,
            beneficialOwnershipForm: updatedBeneficialOwnershipForm,
            // Store transaction data for access in table components
            _transactionData: transactionData,
            // Mark this BO as having changes
            hasChanges: true,
          };
        }
        return bo;
      });

      if (isInManualUpdate) {
        const boWithChanges = result.filter((bo) => bo.hasChanges);
      }

      return result;
    },
    [isInManualUpdate, transactions]
  );

  useEffect(() => {
    // Get processed beneficial owners data (with transaction data if in manual update mode)
    let processedBeneficialOwners = [];
    if (transactions && transactions?.length > 0) {
      processedBeneficialOwners = getBeneficialOwnershipDataForManualUpdate(
        beneficialOwners,
        transactions
      );
    } else if (beneficialOwners && beneficialOwners?.length > 0) {
      processedBeneficialOwners = beneficialOwners;
    } else {
      /** If processed BOs are empty, set to BOs from store
       * This is used in the review workflow for company formation
       */
      processedBeneficialOwners = beneficialOwnersFromStore;
    }

    let tempReportableBO = [];
    let tempNonReportableBO = [];
    if (processedBeneficialOwners.length > 0) {
      processedBeneficialOwners.forEach((bo) => {
        const {
          directPercentShareholding,
          indirectPercentShareholding,
          directPercentVotingRights,
          indirectPercentVotingRights,
          directRightRemoveDirector,
          indirectRightRemoveDirector,
          directCompanyControlRight,
          indirectCompanyControlRight,
        } = bo?.beneficialOwnershipForm ?? {};

        const isDirectBeneficiary =
          directPercentShareholding >= 10 ||
          directPercentVotingRights >= 10 ||
          directRightRemoveDirector === "yes" ||
          directCompanyControlRight === "yes";
        const isIndirectBeneficiary =
          indirectPercentShareholding >= 10 ||
          indirectPercentVotingRights >= 10 ||
          indirectRightRemoveDirector === "yes" ||
          indirectCompanyControlRight === "yes";

        // hide eleventh minority entry on BO table
        if (bo?.idType === "Minority") {
          bo = {
            ...bo,
            hidden: true,
          };
        }

        if (!bo?.hidden === true) {
          const parentBO = bo?.shareholderWithBO
            ? processedBeneficialOwners.find(
                (official) => official._id === bo.shareholderWithBO
              )
            : {};
          if (
            INDIVIDUALS.includes(bo.idType) &&
            bo.isReportable &&
            (CORPORATES.includes(parentBO?.idType)
              ? isDirectBeneficiary
              : isDirectBeneficiary || isIndirectBeneficiary) &&
            bo.isEstateDistributionDetermined !== "No" &&
            bo.idType !== "Estate"
          ) {
            tempReportableBO.push(bo);
          } else tempNonReportableBO.push(bo);
        }
      });
    }

    const verifyKeys = [
      "identificationDetails.nationalIDNumber",
      "identificationDetails.foreignCertificateID",
      "identificationDetails.passportNumber",
      "identificationDetails.birthCertificateNumber",
    ];

    let filteredTempReportableBOs = [...tempReportableBO];
    let filteredTempNonReportableBOs = [...tempNonReportableBO];
    // if one of the multiple instances is reportable shift non-reportable instances to reportable table
    verifyKeys.map((verifyKey) => {
      tempNonReportableBO.map((bo) => {
        // check if current bo has reportable entry/instance
        const hasReportableInstance = tempReportableBO.find(
          (reportableBoRow) =>
            _get(reportableBoRow, verifyKey) &&
            _get(reportableBoRow, verifyKey) === _get(bo, verifyKey)
        );
        if (hasReportableInstance) {
          filteredTempReportableBOs.push(bo);
          const boIndex = filteredTempNonReportableBOs.findIndex(
            (bo) =>
              _get(bo, verifyKey) === _get(hasReportableInstance, verifyKey)
          );
          filteredTempNonReportableBOs.splice(boIndex, 1);
        }
      });
    });

    filteredTempReportableBOs = groupBeneficialOwners(
      _orderBy(
        filteredTempReportableBOs,
        ["shareholderWithBO", "linkToCompany"],
        ["desc", "desc"]
      )
    );

    const jointShareholdersNonReportableBOs =
      filteredTempNonReportableBOs.filter(
        (official) => official.idType === "Joint Shareholder"
      );

    filteredTempNonReportableBOs = groupBeneficialOwners(
      _orderBy(
        filteredTempNonReportableBOs.filter(
          (official) => official.idType !== "Joint Shareholder"
        ),
        ["shareholderWithBO", "linkToCompany"],
        ["desc", "desc"]
      )
    );

    tempReportableBO = filteredTempReportableBOs;
    tempNonReportableBO = filteredTempNonReportableBOs;

    const reportableBOAfterCumulative = tempNonReportableBO
      .filter((bo) => INDIVIDUALS.includes(bo.idType))
      .filter((bo) => {
        const { cumulativeShareholdings, cumulativeVotings } =
          calculateCumulativeOwnership(bo, processedBeneficialOwners);
        if (cumulativeShareholdings >= 10 || cumulativeVotings >= 10) {
          const indexOfBO = tempNonReportableBO.findIndex(
            (beneficialOwner) => beneficialOwner._id === bo._id
          );
          filteredTempNonReportableBOs.splice(indexOfBO, 1);
          return true;
        }
      });

    setReportableBO([
      ...filteredTempReportableBOs,
      ...reportableBOAfterCumulative,
    ]);
    setNonReportableBO([
      ...filteredTempNonReportableBOs,
      ...jointShareholdersNonReportableBOs,
    ]);
  }, [
    beneficialOwners,
    transactions,
    isInManualUpdate,
    getBeneficialOwnershipDataForManualUpdate,
    setReportableBO,
    setNonReportableBO,
    beneficialOwnersFromStore,
  ]);

  const selectedTab = tabs[selectedTabIndex];
  let tableData =
    selectedTab.id === "reportableBO" ? reportableBO : nonReportableBO;

  if (groupedBeneficialOwners?.totalCount > 0) {
    tableData =
      selectedTab.id === "reportableBO"
        ? groupedBeneficialOwners?.reportableBOs
        : groupedBeneficialOwners?.nonReportableBOs;
  }

  const goToPrevStep = () => {
    if (isFirstStep) {
      goToPrevParentTab();
    } else {
      setSelectedTabIndex((prev) => prev - 1);
    }
  };

  const goToNextStep = () => {
    if (isLastStep) {
      goToNextParentTab();
    } else {
      setSelectedTabIndex((prev) => prev + 1);
    }
  };

  return (
    <>
      {/* Reportable and Non-Reportable BO tabs */}
      <div className="mb-2 mt-3">
        <Tabs
          tabs={tabs}
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
        />
      </div>
      {/* Reportable and Non-Reportable BO table */}
      {tableData.length === 0 ? (
        <p className="text-center">No data available</p>
      ) : (
        <BeneficialOwnersTableReview
          data={tableData}
          showActionColumn
          isInManualUpdate={isInManualUpdate}
          selectedTableType={selectedTab.id}
          hideEditBtn
          hideDeleteBtn
          showDocumentLabel
          handleBackBtn={() => null}
          transactions={transactions}
          {...props}
        />
      )}

      <StepButtons
        isFirstStep={isFirstStep}
        goToPrevStep={goToPrevStep}
        goToNextStep={goToNextStep}
        prevBtnLabel={"Previous Step"}
        nextBtnLink={null}
        nextBtnLabel={"Confirm Details"}
        isLastStep={isLastStep}
      />
    </>
  );
};

export default BeneficialOwnership;
